import { env } from "$lib/config/env";
import { omitUndefined } from "$lib/graphql/partial";
import { graphqlPerf } from "$lib/graphql/perf.svelte";
import { assertMutationPayloads } from "$lib/graphql/payload";
import { GraphqlError } from "$lib/graphql/graphql-error";

export { GraphqlError } from "$lib/graphql/graphql-error";

export type GraphqlVariables = Record<string, unknown> | undefined;

export interface GraphqlClientOptions {
  endpoint?: string;
  accessToken?: string | null;
  /** Called once on auth failure; if it returns true, the request is retried with a fresh token getter. */
  onUnauthorized?: () => Promise<boolean>;
  /** Prefer over a stale `accessToken` snapshot when retrying after refresh. */
  getAccessToken?: () => string | null;
  fetch?: typeof globalThis.fetch;
  headers?: Record<string, string>;
}

type DocumentInput = string | { toString(): string; loc?: { source: { body: string } } };

function documentToString(document: DocumentInput): string {
  if (typeof document === "string") return document;
  if (document.loc?.source?.body) return document.loc.source.body;
  return String(document);
}

/**
 * Minimal GraphQL HTTP client.
 * Variables are passed through `omitUndefined` so partial updates omit
 * untouched fields (UNSET) while keeping explicit `null` clears.
 */
export function createGraphqlClient(options: GraphqlClientOptions = {}) {
  const endpoint = options.endpoint ?? env.graphqlEndpoint;
  const fetchImpl = options.fetch ?? globalThis.fetch;

  async function request<TData>(
    document: DocumentInput,
    variables?: GraphqlVariables,
    init?: { accessToken?: string | null; skipAuthRetry?: boolean },
  ): Promise<TData> {
    const queryText = documentToString(document);
    const started = performance.now();
    const token =
      init?.accessToken !== undefined
        ? init.accessToken
        : (options.getAccessToken?.() ?? options.accessToken ?? null);
    const cleaned = variables === undefined ? undefined : omitUndefined(variables);

    try {
      const response = await fetchImpl(endpoint, {
        method: "POST",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
        body: JSON.stringify({ query: queryText, variables: cleaned }),
        credentials: "include",
      });

      let payload: {
        data?: TData;
        errors?: { message: string; extensions?: Record<string, unknown> }[];
      };

      try {
        payload = (await response.json()) as typeof payload;
      } catch {
        const err = new GraphqlError(`GraphQL HTTP ${response.status}`, [], response.status);
        graphqlPerf.record({
          document: queryText,
          ms: performance.now() - started,
          ok: false,
          status: response.status,
        });
        throw err;
      }

      if (!response.ok || payload.errors?.length) {
        const err = new GraphqlError(
          payload.errors?.[0]?.message ?? `GraphQL HTTP ${response.status}`,
          payload.errors ?? [],
          response.status,
        );

        if (!init?.skipAuthRetry && err.isUnauthorized && options.onUnauthorized) {
          const refreshed = await options.onUnauthorized();
          if (refreshed) {
            return request<TData>(document, variables, { ...init, skipAuthRetry: true });
          }
        }

        graphqlPerf.record({
          document: queryText,
          ms: performance.now() - started,
          ok: false,
          status: response.status,
        });
        throw err;
      }

      if (payload.data === undefined) {
        const err = new GraphqlError("GraphQL response missing data", [], response.status);
        graphqlPerf.record({
          document: queryText,
          ms: performance.now() - started,
          ok: false,
          status: response.status,
        });
        throw err;
      }

      assertMutationPayloads(payload.data);

      graphqlPerf.record({
        document: queryText,
        ms: performance.now() - started,
        ok: true,
        status: response.status,
      });
      return payload.data;
    } catch (err) {
      if (!(err instanceof GraphqlError)) {
        graphqlPerf.record({
          document: queryText,
          ms: performance.now() - started,
          ok: false,
        });
      }
      throw err;
    }
  }

  return { endpoint, request };
}

export type GraphqlClient = ReturnType<typeof createGraphqlClient>;
